import { useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CompanyNotaFiscalListItem } from './types';

export interface CompanyNotaFiscalAcoesMenuProps {
  row: CompanyNotaFiscalListItem;
  onMostrar: () => void;
  onUpload: () => void;
  onCancelar: () => void;
  onEventos: () => void;
  onReenviar: () => void;
}

function isErroEnvio(statusEnvio: string): boolean {
  return statusEnvio === 'ERR' || statusEnvio === 'EER';
}

/**
 * Menu de ações da linha, primeira coluna da listagem. Substitui os ícones
 * à direita, que saíam da tela na rolagem horizontal.
 */
export function CompanyNotaFiscalAcoesMenu({
  row,
  onMostrar,
  onUpload,
  onCancelar,
  onEventos,
  onReenviar,
}: CompanyNotaFiscalAcoesMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = (): void => setAnchor(null);

  const statusEnvio = row.statusEnvio || '';
  const canCancelar = row.status === 'EMI' || (row.status === 'PEM' && isErroEnvio(statusEnvio));
  const canUpload = row.status === 'EMI' || row.status === 'PEM';
  const isPca = row.status === 'PCA';
  const hasEventos = (row.eventosCount ?? 0) > 0;
  const canReenviar = isErroEnvio(statusEnvio) && row.status === 'PEM';
  const numero = row.numero ?? row.id;

  function run(action: () => void): void {
    close();
    action();
  }

  return (
    <>
      <Button
        size="small"
        variant="text"
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-label={t('features.empresa.notasFiscais.actions.menuAria', { numero })}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        {t('features.empresa.notasFiscais.actions.menu')}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem onClick={() => run(onMostrar)}>
          {t('features.empresa.notasFiscais.actions.mostrarItem')}
        </MenuItem>
        {canReenviar && (
          <MenuItem onClick={() => run(onReenviar)}>
            {t('features.empresa.notasFiscais.actions.reenviarItem')}
          </MenuItem>
        )}
        {hasEventos && (
          <MenuItem onClick={() => run(onEventos)}>
            {t('features.empresa.notasFiscais.actions.eventosItem', { count: row.eventosCount })}
          </MenuItem>
        )}
        {canUpload && !isPca && (
          <MenuItem onClick={() => run(onUpload)}>
            {t('features.empresa.notasFiscais.actions.uploadItem')}
          </MenuItem>
        )}
        {canCancelar && !isPca && (
          <MenuItem onClick={() => run(onCancelar)}>
            {t('features.empresa.notasFiscais.actions.cancelarItem')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
